/**
 * withComponentList HOC
 *
 * Wraps the root <App> to manage the tenant component catalog lifecycle:
 *  - On login  → fetches /api/tenants-admin-components and saves two localStorage entries:
 *                  eves_component_list      — all "name:version" strings
 *                  eves_base_component_list — only entries where is_base_component === true
 *  - On logout → clears both entries so stale data never leaks to the next session.
 *
 * The query runs in the background and does not block navigation.
 * All callers share the same React Query cache (COMPONENT_LIST_QUERY_KEY).
 */

import { useEffect, useRef, useState } from "react";
import {
  removeComponentList,
  removeBaseComponentList,
  EVES_AUTH_LOGIN_EVENT,
} from "@/common/utils/componentListService";
import { useComponentListQuery } from "@/common/hooks/useComponentListQuery";

interface WithComponentListProps {
  [key: string]: any;
}

/**
 * HOC factory. Attach to the root component in src/index.tsx:
 *
 *   export default withComponentList(App);
 *
 * After login the component list is available via:
 *   - getComponentList()         → string[]        (all components)
 *   - getBaseComponentList()     → string[]        (is_base_component === true only)
 *   - getComponentValueSafe(ComponentKeys.ANALYTICS) → "INT - Analytics:1.0"
 */
const withComponentList = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => {
  return function WithComponentList(props: P & WithComponentListProps) {
    // Derive initial auth state from localStorage so the query is enabled immediately
    // on a hard refresh where access_token is already present.
    const [hasToken, setHasToken] = useState(
      () => !!localStorage.getItem("access_token")
    );

    const { refetch } = useComponentListQuery({ enabled: hasToken });

    // Keep refetch stable across re-renders so the event listener closure
    // always calls the latest version without re-registering.
    const refetchRef = useRef(refetch);
    refetchRef.current = refetch;

    // Listen for the eves:auth-login custom event dispatched by dispatchAuthLoginEvent()
    // after a successful login. Enables the query and triggers an immediate fetch.
    useEffect(() => {
      const onAuthLogin = () => {
        setHasToken(true);
        refetchRef.current();
      };
      window.addEventListener(EVES_AUTH_LOGIN_EVENT, onAuthLogin);
      return () => window.removeEventListener(EVES_AUTH_LOGIN_EVENT, onAuthLogin);
    }, []);

    // Clear both component lists from localStorage whenever auth is lost.
    useEffect(() => {
      if (!hasToken) {
        removeComponentList();
        removeBaseComponentList();
      }
    }, [hasToken]);

    // Cross-tab logout: when access_token is removed in another tab, clear lists here too.
    useEffect(() => {
      const handleStorageChange = (e: StorageEvent) => {
        if (e.key === "access_token" && !e.newValue) {
          removeComponentList();
          removeBaseComponentList();
          setHasToken(false);
        }
      };

      window.addEventListener("storage", handleStorageChange);
      return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    return <WrappedComponent {...(props as P)} />;
  };
};

export default withComponentList;
